export type GattSection = "att" | "cmd" | "sts" | "dta" | "dia";

export interface GattMeta {
  att?: Record<string, string>;
  cmd?: Record<string, string>;
  sts?: Record<string, string>;
  dta?: Record<string, string>;
  dia?: Record<string, string>;
}

export interface AvatarAttributeRow {
  section: GattSection;
  sectionLabel: string;
  prop: string;
  value: string;
  description: string;
}

const SECTION_LABELS: Record<GattSection, string> = {
  att: "Attributes",
  cmd: "Commands",
  sts: "States",
  dta: "Data",
  dia: "Diagnostics",
};

export function parseGattMeta(raw: unknown): GattMeta | undefined {
  if (!raw) return undefined;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as GattMeta;
    } catch {
      return undefined;
    }
  }
  if (typeof raw === "object") {
    return raw as GattMeta;
  }
  return undefined;
}

function normalizePropKey(prop: string): string {
  return prop.replace(/^_/, "").toLowerCase();
}

export function lookupGattDescription(
  sectionMap: Record<string, string> | undefined,
  prop: string | null | undefined
): string | null {
  if (!sectionMap || !prop?.trim()) return null;

  const trimmed = prop.trim();
  const direct =
    sectionMap[trimmed] ??
    sectionMap[trimmed.toLowerCase()] ??
    sectionMap[trimmed.startsWith("_") ? trimmed.slice(1) : `_${trimmed}`];
  if (direct) return direct;

  const target = normalizePropKey(trimmed);
  for (const [key, description] of Object.entries(sectionMap)) {
    if (key.startsWith("_comment")) continue;
    if (normalizePropKey(key) === target) return description;
  }
  return null;
}

export function buildAvatarAttributeRows(
  avatar: {
    att?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    cmd?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    sts?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    dta?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
    dia?: Array<{ prop?: string | null; value?: string | null; meta?: string | null }> | null;
  },
  gattMeta?: GattMeta
): AvatarAttributeRow[] {
  const sections: GattSection[] = ["att", "cmd", "sts", "dta", "dia"];
  const rows: AvatarAttributeRow[] = [];

  for (const section of sections) {
    const attributes = avatar[section];
    if (!attributes?.length) continue;

    const sectionMap = gattMeta?.[section];
    const sectionLabel = SECTION_LABELS[section];

    for (const attribute of attributes) {
      const prop = attribute.prop?.trim() || "—";
      const value =
        attribute.value !== null && attribute.value !== undefined
          ? String(attribute.value)
          : "—";
      const fromGatt = lookupGattDescription(sectionMap, attribute.prop);
      const description =
        fromGatt || attribute.meta?.trim() || "No description available";

      rows.push({
        section,
        sectionLabel,
        prop,
        value,
        description,
      });
    }
  }

  return rows;
}
